import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Spinner } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { DocumentUploadCard } from '../../components/dashboard/DocumentUploadCard';
import { api } from '../../lib/api';
import type { Order, Invoice } from '@bharat-consultancy/shared';

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.orders
      .get(id)
      .then((res) => {
        setOrder(res.order);
        setInvoice(res.invoice);
      })
      .catch(() => setLoadError('Could not load this order. Please refresh and try again.'))
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-seal-blue" />
      </div>
    );
  }

  if (loadError) {
    return <p className="text-red-600">{loadError}</p>;
  }

  if (!order) {
    return <p className="text-ink/60">Order not found.</p>;
  }

  const showDocumentUpload = ['documents_requested', 'documents_received', 'assigned'].includes(order.status);

  return (
    <>
      <SEOHead title={`Order ${order.orderNumber} — Bharat Consultancy`} description="" canonicalPath={`/dashboard/orders/${order.id}`} />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">{order.service?.title ?? 'Service'}</h1>
          <p className="mt-1 font-mono text-sm text-ink/50">{order.orderNumber}</p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-lg border border-line bg-white p-5">
            <h2 className="font-display text-base font-semibold text-ink">Booking Details</h2>
            <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-ink/50">Contact Name</dt>
                <dd className="text-ink">{order.contactName}</dd>
              </div>
              <div>
                <dt className="text-ink/50">Mobile</dt>
                <dd className="text-ink">{order.contactMobile}</dd>
              </div>
              <div>
                <dt className="text-ink/50">Callback Date</dt>
                <dd className="text-ink">{order.callbackDate ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-ink/50">Callback Time</dt>
                <dd className="text-ink">{order.callbackTime ?? '—'}</dd>
              </div>
              {order.requirementSummary && (
                <div className="sm:col-span-2">
                  <dt className="text-ink/50">Requirement</dt>
                  <dd className="text-ink">{order.requirementSummary}</dd>
                </div>
              )}
            </dl>
          </div>

          {showDocumentUpload && (
            <div className="rounded-lg border border-line bg-white p-5">
              <h2 className="font-display text-base font-semibold text-ink">Document Uploads</h2>
              <p className="mt-1 text-sm text-ink/60">Upload the documents requested for this order.</p>
              <div className="mt-4 space-y-3">
                <DocumentUploadCard orderId={order.id} docName="PAN Card" />
                <DocumentUploadCard orderId={order.id} docName="Aadhaar Card" />
                <DocumentUploadCard orderId={order.id} docName="Other Supporting Document" />
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-line bg-white p-5">
            <h2 className="font-display text-base font-semibold text-ink">Payment</h2>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink/50">Subtotal</dt>
                <dd className="font-mono text-ink">₹{order.baseAmount.toLocaleString('en-IN')}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink/50">Tax</dt>
                <dd className="font-mono text-ink">₹{order.taxAmount.toLocaleString('en-IN')}</dd>
              </div>
              <div className="stub-divider flex justify-between pt-2 font-semibold">
                <dt className="text-ink">Total</dt>
                <dd className="font-mono text-seal-blue">₹{order.totalAmount.toLocaleString('en-IN')}</dd>
              </div>
            </dl>

            {invoice && (
              <a href={api.invoices.downloadUrl(order.id)} target="_blank" rel="noreferrer" className="mt-4 block">
                <Button variant="outline" className="w-full">
                  Download Invoice
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
